import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import blogOptions from '../../../content/blog-tags.json'
import { type BlogFiltersState, type SortOrder } from '../components/BlogFilters'
import { allBlogPosts } from '../data/blogPostsData'

const POSTS_PER_PAGE = 6

const EMPTY_FILTERS: BlogFiltersState = {
  date: null,
  region: null,
  topic: null,
  category: null,
}

const FILTER_OPTIONS: Record<keyof BlogFiltersState, string[]> = {
  date: ['month', 'quarter'],
  region: blogOptions.regions.map((region) => region.name),
  topic: blogOptions.topics.map((topic) => topic.name),
  category: blogOptions.categories.map((category) => category.name),
}

const FILTER_KEYS = Object.keys(FILTER_OPTIONS) as Array<keyof BlogFiltersState>

function getFiltersFromSearchParams(searchParams: URLSearchParams): BlogFiltersState {
  const filters = { ...EMPTY_FILTERS }

  for (const key of FILTER_KEYS) {
    const value = searchParams.get(key)
    filters[key] = value && FILTER_OPTIONS[key].includes(value) ? value : null
  }

  return filters
}

/** Parse frontmatter date for filtering/sorting; invalid dates → 0 so NaN never breaks comparisons. */
function getPostDateMs(date: unknown): number {
  if (date == null || date === '') return 0
  const t = new Date(date as string | number | Date).getTime()
  return Number.isFinite(t) ? t : 0
}

const useBlogData = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const filters = useMemo(() => getFiltersFromSearchParams(searchParams), [searchParams])
  const [sortOrder, setSortOrder] = useState<SortOrder>('newest')
  const [currentPage, setCurrentPage] = useState(1)

  const setFilters = (nextFilters: BlogFiltersState) => {
    setSearchParams((currentParams) => {
      const nextParams = new URLSearchParams(currentParams)

      for (const key of FILTER_KEYS) {
        const value = nextFilters[key]
        if (value && FILTER_OPTIONS[key].includes(value)) {
          nextParams.set(key, value)
        } else {
          nextParams.delete(key)
        }
      }

      return nextParams
    })
  }

  const regions = FILTER_OPTIONS.region
  const topics = FILTER_OPTIONS.topic
  const categories = FILTER_OPTIONS.category

  const filteredAndSortedPosts = useMemo(() => {
    let result = [...allBlogPosts]

    if (filters.date) {
      const now = new Date()
      const cutoff = new Date(now)
      if (filters.date === 'month') {
        cutoff.setMonth(cutoff.getMonth() - 1)
      } else if (filters.date === 'quarter') {
        cutoff.setMonth(cutoff.getMonth() - 3)
      }
      const cutoffTime = cutoff.getTime()
      result = result.filter((p) => {
        const d = getPostDateMs(p.frontmatter.date)
        return d >= cutoffTime
      })
    }
    if (filters.region) {
      result = result.filter((p) => p.frontmatter.region === filters.region)
    }
    if (filters.topic) {
      result = result.filter((p) => p.frontmatter.topic === filters.topic)
    }
    if (filters.category) {
      result = result.filter((p) => p.frontmatter.category === filters.category)
    }

    result.sort((a, b) => {
      const dateA = getPostDateMs(a.frontmatter.date)
      const dateB = getPostDateMs(b.frontmatter.date)
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB
    })

    return result
  }, [filters, sortOrder])

  const totalPages = Math.max(1, Math.ceil(filteredAndSortedPosts.length / POSTS_PER_PAGE))

  useEffect(() => {
    setCurrentPage(1)
  }, [filters, sortOrder])

  /** No posts → drop filter UI state so nothing remains selected. */
  useEffect(() => {
    if (allBlogPosts.length === 0) {
      setFilters(EMPTY_FILTERS)
      setCurrentPage(1)
    }
  }, [allBlogPosts.length])

  // Clamp for slice + pagination when state is past last page (e.g. list shrinks); avoids fighting filter reset.
  const effectivePage = Math.min(Math.max(1, currentPage), totalPages)
  const paginatedPosts = filteredAndSortedPosts.slice(
    (effectivePage - 1) * POSTS_PER_PAGE,
    effectivePage * POSTS_PER_PAGE
  )

  const activeFilters = [
    filters.date && {
      key: 'date' as const,
      label:
        filters.date === 'month'
          ? 'This month'
          : filters.date === 'quarter'
            ? 'Last 3 months'
            : filters.date,
    },
    filters.region && { key: 'region' as const, label: filters.region },
    filters.topic && { key: 'topic' as const, label: filters.topic },
    filters.category && {
      key: 'category' as const,
      label: filters.category,
    },
  ].filter(Boolean) as Array<{ key: keyof BlogFiltersState; label: string }>

  return {
    filters,
    setFilters,
    sortOrder,
    setSortOrder,
    currentPage: effectivePage,
    setCurrentPage,
    paginatedPosts,
    totalPages,
    activeFilters,
    regions,
    topics,
    categories,
    /** Zero when there are no markdown posts yet — Blog page shows the Insights “under construction” fallback. */
    totalPostCount: allBlogPosts.length,
  }
}

export default useBlogData
